import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewChild, ChangeDetectorRef } from '@angular/core';
import { AuthService } from 'src/app/core/services/auth.service';

@Component({
    selector: 'app-email-html-editor',
    templateUrl: './email-html-editor.component.html',
    styleUrls: ['./email-html-editor.component.css']
})
export class EmailHtmlEditorComponent implements OnInit, OnChanges {

    // @Input() htmlContent: string = '';
    @Input() candidateInfoList;
    @Input() context;
    @Input() displayedNote;
    @Input() documentsSectionHtml: string = '';
    @Input() hideIncludeDetails: boolean = false;
    @Input() externalCandidateAction: { type: 'single' | 'selectAll'; candidate?: any; selectAll?: boolean; timestamp?: number } = null;
    @Output() htmlContentChange = new EventEmitter<string>();
    @Output() selectedFieldsChange = new EventEmitter<any[]>();
    @Output() candidateSelectionSync = new EventEmitter<any[]>();
    @ViewChild('editorComponent') editorComponent: any;
    htmlContent: string = '';
    editorConfig: any;
    private savedSelection: Range | null = null;
    private _lastEmittedContent: string | null = null;
    selectAllCandidates: boolean = false;
    showAllCandidates: boolean = false;
    loggedInUserName: string = (this.authService.getUserDetails().firstname || '') + (this.authService.getUserDetails().lastname || '');
    candidateInformationList: any[] = [];
    selectAllCandidatesNC:boolean = false;
    addedCandidateKeys:any = {}; // Track added candidate keys

    constructor(
        private authService:AuthService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['displayedNote'] && !changes['displayedNote'].isFirstChange()) {
            const newVal = changes['displayedNote'].currentValue;
            if (newVal != null && newVal !== this._lastEmittedContent) {
                this.htmlContent = newVal;
                this.cdr.detectChanges();
            }
        }

        if (changes['documentsSectionHtml'] && !changes['documentsSectionHtml'].firstChange) {
            setTimeout(() => {
                this._updateDocumentsSection(this.documentsSectionHtml);
                this._syncCandidateDetailsHeader();
            }, 100);
        }

        if (changes['externalCandidateAction']) {
            const action = this.externalCandidateAction;
            if (action) {
                if (action.type === 'selectAll') {
                    this.selectAllCandidates = action.selectAll;
                    if (this.candidateInformationList) {
                        this.candidateInformationList.forEach(c => c.selected = action.selectAll);
                    }
                    this.onSelectAllCandidates();
                } else if (action.type === 'single' && action.candidate) {
                    const internalCandidate = this.candidateInformationList &&
                        this.candidateInformationList.find(c => c.id === action.candidate.id);
                    if (internalCandidate) {
                        internalCandidate.selected = action.candidate.selected;
                        this.onCandidateCheckboxChange(internalCandidate);
                    }
                }
            }
        }
    }

    ngAfterViewInit(): void {
        // Listen for focus on the editor to save selection
        const editorElement = document.querySelector('app-ngx-editor [contenteditable="true"]') as HTMLElement;
        if (editorElement) {
            editorElement.addEventListener('mouseup', () => {
                this.saveSelection();
            });
            editorElement.addEventListener('keyup', () => {
                this.saveSelection();
            });
        }

        if (this.context != 'user-communication' && this.context != 'notes') {
            this._setEmailBodyPrePopulatedInformation(true);
        }

        this.displayedNote = this.displayedNote || '';
    }

    _setEmailBodyPrePopulatedInformation(addCandidateInformation) {
        let prePopulatedEmailSignature = `<p>Hello,</p><p><br/></p><p><br/></p><p><br/></p><p id="signature">Thank You.</p><p><b>${this.loggedInUserName}</b></p><br/><br/>`;
        this.htmlContent = prePopulatedEmailSignature;
    }

    saveSelection(): void {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            this.savedSelection = selection.getRangeAt(0).cloneRange();
        }
    }

    restoreSelection(): void {
        if (this.savedSelection) {
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(this.savedSelection);
        }
    }

    /**
     * Inserts html immediately after the "Hello," paragraph in the editor.
     * If existing candidate spans are present, inserts after the last one.
     */
    private insertHtmlAfterHelloParagraph(html: string, editorElement: HTMLElement): boolean {
        const paragraphs = editorElement.querySelectorAll('p');
        let helloParagraph: Element | null = null;
        paragraphs.forEach(p => {
            if (p.textContent && p.textContent.trim().toLowerCase() === 'hello,') {
                helloParagraph = p;
            }
        });
        if (!helloParagraph) return false;

        // Anchor to the last existing candidate span (+ its trailing br/nbsp) if present,
        // otherwise anchor to the Hello paragraph itself.
        let insertAfterNode: Node = helloParagraph;
        const existingCandidates = editorElement.querySelectorAll('span[id^="candidate-"]');
        if (existingCandidates.length > 0) {
            let lastNode: Node = existingCandidates[existingCandidates.length - 1];
            let next = lastNode.nextSibling;
            while (next && next.nodeType === 1 &&
                ((next as HTMLElement).tagName === 'BR' ||
                 (next as HTMLElement).outerHTML === '<span>&nbsp;</span>')) {
                lastNode = next;
                next = lastNode.nextSibling;
            }
            insertAfterNode = lastNode;
        }

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        let lastInserted: Node = insertAfterNode;
        while (tempDiv.firstChild) {
            const node = tempDiv.firstChild;
            if (lastInserted.parentNode) {
                lastInserted.parentNode.insertBefore(node, lastInserted.nextSibling);
                lastInserted = node;
            } else {
                break;
            }
        }
        return true;
    }


    ngOnInit(): void {
        this.editorConfig = {
            toolbar: [
                ['bold', 'italic', 'underline', 'strikeThrough'],
                ['fontSize'],
                ['orderedList', 'unorderedList'],
                ['undo', 'redo'],
                ['paragraph'],
                ['blockquote'],
                ['removeFormat']
            ]
        };
        this._setCandidateInformationInBody();
        this._handleFilterDisplay();
    }

    //  Emit model changes from the inner editor so parent can update.
    onEditorModelChange(value: string): void {
        this.htmlContent = value;
        this._lastEmittedContent = this.htmlContent;
        // emit for parent bindings
        this.htmlContentChange.emit(this.htmlContent);
        // keep candidate-checkbox state in sync
        this.syncCandidateCheckboxesWithEditor(this.htmlContent);
    }

    // Reset the editor content to empty and notify parents.
    resetEditor(): void {
        this.htmlContent = '';
        this.displayedNote = '';
        // Reset note-context checkbox state
        this.selectAllCandidatesNC = false;
        this.addedCandidateKeys = {};
        if (this.candidateInformationList) {
            this.candidateInformationList.forEach(c => { c.selectedForNote = false; });
        }
        // update inner editor via two-way binding and notify parent
        this.htmlContentChange.emit(this.htmlContent);
        this.syncCandidateCheckboxesWithEditor(this.htmlContent);
        // Force Angular change detection so checkbox UI updates even when called from outside the zone
        this.cdr.detectChanges();
        // if we have direct access to child editor component, try to reset it as well
        try {
            if (this.editorComponent && typeof this.editorComponent.writeValue === 'function') {
                this.editorComponent.writeValue('');
            }
        } catch (e) {
            // ignore
        }
    }

    _setCandidateInformationInBody() {
        if (!this.candidateInfoList || this.candidateInfoList.length === 0) {
            this.candidateInformationList = [];
            let candidateInformation = ['name', 'phone_number', 'email', 'address', 'current_location', 'current_role', 'current_company', 'seeking_job_type', 'desired_salary.rate', 'sponsorship', 'willing_to_relocate', 'linkedin_url'];
            candidateInformation.map(candidate => {
                let name = candidate.split('_');
                let formattedName = name.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                formattedName = formattedName.replace('.', '/');
                let tempObj = { id: candidate, name: formattedName, value: '', selected: false };
                this.candidateInformationList.push(tempObj);
            });
        } else {
            // Clone and normalize candidate info to ensure 'value' field exists and has actual data
            this.candidateInformationList = _.cloneDeep(this.candidateInfoList).map(candidate => {
                return {
                    id: candidate.id || candidate.fieldKey,
                    name: candidate.name || candidate.fieldLabel,
                    value: candidate.value || candidate.fieldValue || candidate.actualValue || 'Not Specified',
                    selected: candidate.selected || false
                };
            });
        }
    }

    _handleFilterDisplay() {
        ($(document) as any).ready(() => {
            const panel = $("#email-editor-information-panel");
            const chevronDown = $("#email-editor-information-filter .fa-chevron-down");
            const chevronUp = $("#email-editor-information-filter .fa-chevron-up");

            // Initially hide panel and set icon state
            panel.hide();
            chevronUp.hide();
            chevronDown.show();

            // Remove old handlers to avoid duplicates
            $(document)
                .off("click", "#email-editor-information-filter")
                .on("click", "#email-editor-information-filter", () => {
                    // Toggle the panel
                    panel.animate({ height: "toggle" }, 200, () => {
                        // After animation completes, toggle icon visibility
                        if (panel.is(":visible")) {
                            chevronDown.hide();
                            chevronUp.show();
                        } else {
                            chevronUp.hide();
                            chevronDown.show();
                        }
                    });
                });
        });
    }

    onInsertField(field: string): void {
        if (!field) return;
        // Try to insert at cursor using execCommand
        const textToInsert = `{{${field}}}`;
        // Find the contenteditable div inside ngx-editor
        const editorElement = document.querySelector('app-ngx-editor [contenteditable="true"]') as HTMLElement;
        if (editorElement) {
            editorElement.focus();
            document.execCommand('insertText', false, textToInsert);
        } else {
            // fallback: append at end
            this.htmlContent += textToInsert;
        }
    }

    onSelectAllCandidates(): void {
        this.candidateInformationList.forEach(candidate => {
            candidate.selected = this.selectAllCandidates;
        });
        const editorElement = document.querySelector('app-ngx-editor [contenteditable="true"]') as HTMLElement;
        if (this.selectAllCandidates) {
            // Insert each candidate info as a separate root-level span at the cursor, only if not already present
            if (editorElement) {
                editorElement.focus();

                if (!this.savedSelection) {
                    // No cursor position detected – insert each field after the Hello paragraph
                    let isFirstCandidate = true;
                    this.candidateInformationList.forEach(candidate => {
                        if (!editorElement.querySelector(`span[id='candidate-${candidate.id}']`)) {
                            const prefix = isFirstCandidate ? '<br/>' : '';
                            const html = `${prefix}<span id="candidate-${this.escapeHtml(candidate.id)}" contenteditable="true"><b style="background-color: transparent;"> ${this.escapeHtml(candidate.name)}:</b><span style="background-color: transparent;"> ${this.escapeHtml(candidate.value || 'Not Specified')}</span></span><span>&nbsp;</span><br/>`;
                            isFirstCandidate = false;
                            if (!this.insertHtmlAfterHelloParagraph(html, editorElement)) {
                                this.htmlContent += html;
                            }
                        }
                    });
                } else {
                    this.restoreSelection();
                    const selection = window.getSelection();
                    if (selection && selection.rangeCount > 0) {
                        let range = selection.getRangeAt(0);
                        let isFirstCandidate = true;
                        this.candidateInformationList.forEach(candidate => {
                            // Check if candidate info already exists in editor
                            if (!editorElement.querySelector(`span[id='candidate-${candidate.id}']`)) {
                                const tempDiv = document.createElement('div');
                                // Add line break before first candidate to start on new line
                                const prefix = isFirstCandidate ? '<br/>' : '';
                                tempDiv.innerHTML = `${prefix}<span id="candidate-${this.escapeHtml(candidate.id)}" contenteditable="true"><b style="background-color: transparent;"> ${this.escapeHtml(candidate.name)}:</b><span style="background-color: transparent;"> ${this.escapeHtml(candidate.value || 'Not Specified')}</span></span><span>&nbsp;</span><br/>`; // Add space after span
                                isFirstCandidate = false;
                                while (tempDiv.firstChild) {
                                    const node = tempDiv.firstChild;
                                    range.collapse(false);
                                    range.insertNode(node);
                                    range.setStartAfter(node);
                                    range.setEndAfter(node);
                                }
                            }
                        });
                        selection.removeAllRanges();
                        selection.addRange(range);
                        this.saveSelection();
                    } else {
                        // Append at end
                        this.candidateInformationList.forEach(candidate => {
                            // Check if candidate info already exists in htmlContent
                            if (!this.htmlContent.includes(`id="candidate-${candidate.id}"`)) {
                                this.htmlContent += `<span id="candidate-${this.escapeHtml(candidate.id)}" contenteditable="true"><b style="background-color: transparent;"> ${this.escapeHtml(candidate.name)}:</b><span style="background-color: transparent;"> ${this.escapeHtml(candidate.value || 'Not Specified')}</span></span><span>&nbsp;</span>`; // Add space after span
                            }
                        });
                    }
                }

                // Keep caret near candidate section instead of jumping to start.
                this.setCursorAfterCandidateSection(editorElement);
            } else {
                // Append at end
                this.candidateInformationList.forEach(candidate => {
                    if (!this.htmlContent.includes(`id="candidate-${candidate.id}"`)) {
                        this.htmlContent += `<span id="candidate-${this.escapeHtml(candidate.id)}" contenteditable="true"><b style="background-color: transparent;"> ${this.escapeHtml(candidate.name)}:</b><span style="background-color: transparent;"> ${this.escapeHtml(candidate.value || 'Not Specified')}</span></span><span>&nbsp;</span>`; // Add space after span
                    }
                });
            }
        } else {
            // If unchecking select all, remove all candidate info
            this.candidateInformationList.forEach(candidate => {
                this.removeCandidateInfo(candidate);
            });
            setTimeout(() => {
                editorElement.innerHTML = editorElement.innerHTML.replace(
                    /(<span(?![^>]*id=")[^>]*>.*?<\/span>)\s*<span>&nbsp;<\/span><br\s*\/?>/gi,
                    '$1'
                );
                // Keep caret near candidate section instead of jumping to start.
                this.setCursorAfterCandidateSection(editorElement);
            }, 10);
        }
        setTimeout(() => {
            // Read live DOM content and emit to parent WITHOUT updating this.htmlContent.
            // causing writeValue() to re-render and reset the cursor to position 0.
            const editorEl = document.querySelector('app-ngx-editor [contenteditable="true"]') as HTMLElement;
            const liveContent = editorEl ? editorEl.innerHTML : this.htmlContent;
            this.htmlContentChange.emit(liveContent);
            this.emitSelectedFields();
            this._syncCandidateDetailsHeader();
            // Re-apply cursor after header DOM insertion; inserting the <p> header node before
            // candidate spans can collapse the browser selection back to position 0.
            if (this.selectAllCandidates && editorEl) {
                this.setCursorAfterCandidateSection(editorEl);
            }
        }, 200);
    }

    setCursorAfterCandidateSection(editorElement: HTMLElement): void {
        if (!editorElement) {
            return;
        }

        let anchorNode: Node | null = null;
        const candidateSpans = editorElement.querySelectorAll('span[id^="candidate-"]');
        if (candidateSpans.length > 0) {
            anchorNode = candidateSpans[candidateSpans.length - 1];
        }

        if (!anchorNode) {
            anchorNode = editorElement.querySelector('#candidate-details-header');
        }

        if (!anchorNode) {
            const paragraphs = editorElement.querySelectorAll('p');
            paragraphs.forEach(p => {
                if (!anchorNode && p.textContent && p.textContent.trim().toLowerCase() === 'hello,') {
                    anchorNode = p;
                }
            });
        }

        if (!anchorNode) {
            return;
        }

        let lastNode: Node = anchorNode;
        let nextNode = anchorNode.nextSibling;

        // Include the trailing nbsp/br nodes so caret lands at the end of the inserted block.
        while (
            nextNode &&
            nextNode.nodeType === 1 &&
            ((nextNode as HTMLElement).tagName === 'BR' ||
                (nextNode as HTMLElement).outerHTML === '<span>&nbsp;</span>')
        ) {
            lastNode = nextNode;
            nextNode = lastNode.nextSibling;
        }

        const selection = window.getSelection();
        if (!selection) {
            return;
        }

        const range = document.createRange();
        range.setStartAfter(lastNode);
        range.setEndAfter(lastNode);

        editorElement.focus();
        selection.removeAllRanges();
        selection.addRange(range);
        this.savedSelection = range.cloneRange();
    }

    onCandidateCheckboxChange(candidate: any, fromSelectAll: boolean = false): void {
        // Store selected state safely
        const selected = candidate.selected;

        setTimeout(() => {
            if (selected) {
                this.insertCandidateInfoAtCursor(candidate);
                setTimeout(() => {
                    this.insertCursorAfterLastCandidate();
                }, 60);
            } else if (!fromSelectAll) {
                this.removeCandidateInfo(candidate);
            }
        }, 50);

        setTimeout(() => {
            // Read live DOM content and emit to parent WITHOUT updating this.htmlContent.
            // causing writeValue() to re-render and reset the cursor to position 0.
            const editorEl = document.querySelector('app-ngx-editor [contenteditable="true"]') as HTMLElement;
            const liveContent = editorEl ? editorEl.innerHTML : this.htmlContent;
            this.htmlContentChange.emit(liveContent);
            this.emitSelectedFields();
            this._syncCandidateDetailsHeader();
            // Re-apply cursor after header DOM insertion to prevent selection collapsing to start.
            const anySelected = this.candidateInformationList && this.candidateInformationList.some(c => c.selected);
            if (anySelected && editorEl) {
                this.setCursorAfterCandidateSection(editorEl);
            }
        }, 200);
    }

    emitSelectedFields(): void {
        const selectedFields = [];
        
        // Get the editor element to extract fields in the order they appear in HTML
        const editorElement = document.querySelector('app-ngx-editor [contenteditable="true"]') as HTMLElement;
        
        if (editorElement) {
            // Get all candidate field spans in the order they appear in the HTML
            const candidateSpans = editorElement.querySelectorAll('span[id^="candidate-"]');
            
            candidateSpans.forEach((span) => {
                const candidateId = span.id.replace('candidate-', '');
                // Find the candidate in candidateInformationList to get complete data
                const candidate = this.candidateInformationList.find(c => c.id === candidateId);
                
                if (candidate) {
                    selectedFields.push({
                        id: candidate.id,
                        name: candidate.name,
                        value: this.getActualCandidateValue(candidate),
                        selected: true
                    });
                }
            });
        } else {
            // Fallback: use candidateInformationList if editor not found
            const fallbackFields = this.candidateInformationList
                .filter(candidate => candidate.selected)
                .map(candidate => ({
                    id: candidate.id,
                    name: candidate.name,
                    value: this.getActualCandidateValue(candidate),
                    selected: true
                }));
            selectedFields.push(...fallbackFields);
        }
        
        this.selectedFieldsChange.emit(selectedFields);
    }

    private escapeHtml(value: any): string {
        return String(value == null ? '' : value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    getActualCandidateValue(candidate: any): string {
        // Extract actual value from candidate object, handling different field name scenarios
        if (!candidate) {
            return 'Not Specified';
        }

        // Try different field names in order of priority
        const value = candidate.value || candidate.fieldValue || candidate.actualValue;
        
        if (value && value.toString().trim() !== '') {
            return value.toString();
        }

        return 'Not Specified';
    }


    insertCandidateInfoAtCursor(candidate: any): void {
        const textToInsert = `<br/><span id="candidate-${this.escapeHtml(candidate.id)}" contenteditable="true"><b style="background-color: transparent;">${this.escapeHtml(candidate.name)}:</b><span style="background-color: transparent;"> ${this.escapeHtml(candidate.value || 'Not Specified')}</span></span>`;
        const editorElement = document.querySelector('app-ngx-editor [contenteditable="true"]') as HTMLElement;
        
        if (editorElement) {
            // Check if this candidate already exists in the editor
            const existingSpan = editorElement.querySelector(`span[id='candidate-${candidate.id}']`);
            if (existingSpan) {
                return; // Don't insert duplicates
            }
            
            editorElement.focus();

            // If the user never placed the cursor in the editor, insert after Hello paragraph
            if (!this.savedSelection) {
                if (!this.insertHtmlAfterHelloParagraph(textToInsert, editorElement)) {
                    this.htmlContent += textToInsert;
                }
                return;
            }

            this.restoreSelection();
            const selection = window.getSelection();
            
            if (selection && selection.rangeCount > 0) {
                let range = selection.getRangeAt(0);
                
                // Insert the nodes at the current cursor position
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = textToInsert;
                
                while (tempDiv.firstChild) {
                    const node = tempDiv.firstChild;
                    if (node) {
                        range.collapse(false); // Collapse to end of current position
                        range.insertNode(node);
                        // Move range after inserted node for next insert
                        range = document.createRange();
                        range.setStartAfter(node);
                        range.setEndAfter(node);
                    }
                }
                
                selection.removeAllRanges();
                selection.addRange(range);
                this.saveSelection();
            } else {
                // No selection - check if there are existing candidate spans
                const allCandidateSpans = editorElement.querySelectorAll('span[id^="candidate-"]');
                if (allCandidateSpans.length > 0) {
                    // Insert after the last candidate span
                    const lastCandidateSpan = allCandidateSpans[allCandidateSpans.length - 1];
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = textToInsert;
                    while (tempDiv.firstChild) {
                        lastCandidateSpan.parentNode.insertBefore(tempDiv.firstChild, lastCandidateSpan.nextSibling);
                    }
                } else {
                    // Append at end
                    this.htmlContent += textToInsert;
                }
            }
        } else {
            // Append at end
            this.htmlContent += textToInsert;
        }
    }


    insertCursorAfterLastCandidate(): void {
        const textToInsert = `<span>&nbsp;</span>`;
        const editorElement = document.querySelector('app-ngx-editor [contenteditable="true"]') as HTMLElement;
        if (editorElement) {
            editorElement.focus();
            this.restoreSelection();
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
                let range = selection.getRangeAt(0);
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = textToInsert;
                const node = tempDiv.firstChild;
                if (node) {
                    // If inside a span, move range after the parent span
                    let container = range.startContainer;
                    let parentSpan = null;
                        if (container.nodeType === 3) { // text node
                            parentSpan = container.parentElement.closest('span[id^="candidate-"]');
                        } else if (container.nodeType === 1) { // element node
                            parentSpan = (container as HTMLElement).closest && (container as HTMLElement).closest('span[id^="candidate-"]');
                    }
                    if (parentSpan) {
                        range = document.createRange();
                        range.setStartAfter(parentSpan);
                        range.setEndAfter(parentSpan);
                    } else {
                        range.collapse(false);
                    }
                    range.insertNode(node);
                    // Move range after inserted node for next insert
                    range = document.createRange();
                    range.setStartAfter(node);
                    range.setEndAfter(node);
                    selection.removeAllRanges();
                    selection.addRange(range);
                }
                this.saveSelection();
            } else {
                // fallback: append at end
                this.htmlContent += textToInsert;
            }
        } else {
            // fallback: append at end
            this.htmlContent += textToInsert;
        }
        setTimeout(() => {
            this.syncCandidateCheckboxesWithEditor(editorElement.innerHTML);
        }, 100);
    }

    removeCandidateInfo(candidate: any): void {
        // Remove only the candidate info span with the unique id from the editor
        const editorElement = document.querySelector('app-ngx-editor [contenteditable="true"]') as HTMLElement;
        if (editorElement) {
            const spans = editorElement.querySelectorAll(`span[id='candidate-${candidate.id}']`);
            spans.forEach(span => {
                if (span.parentNode) {
                    // Save cursor position before removal
                    const selection = window.getSelection();
                    const range = document.createRange();
                    
                    // Position cursor where the span was (before removal)
                    range.setStartBefore(span);
                    range.collapse(true);
                    
                    // Remove the preceding <br/> only if it is the leading separator that
                    let prev = span.previousSibling;
                    if (prev && prev.nodeType === 1 && (prev as HTMLElement).tagName === 'BR') {
                        const beforePrev = prev.previousSibling;
                        const isCandidateTrailingBr = beforePrev && beforePrev.nodeType === 1 &&
                            (beforePrev as HTMLElement).outerHTML === '<span>&nbsp;</span>';
                        if (!isCandidateTrailingBr) {
                            prev.parentNode.removeChild(prev);
                        }
                    }
                    
                    // Remove the following <span>&nbsp;</span> if present (but keep other spacing)
                    let next = span.nextSibling;
                    if (next && next.nodeType === 1 && (next as HTMLElement).outerHTML === '<span>&nbsp;</span>') {
                        next.parentNode.removeChild(next);
                        // Also remove a trailing <br/> that follows the nbsp in select-all insertions
                        let trailingBr = span.nextSibling;
                        if (trailingBr && trailingBr.nodeType === 1 && (trailingBr as HTMLElement).tagName === 'BR') {
                            trailingBr.parentNode.removeChild(trailingBr);
                        }
                    }
                    
                    // Remove the span itself
                    span.parentNode.removeChild(span);

                    // If the header is now directly adjacent to the next candidate (no <br/> between
                    const headerEl = editorElement.querySelector('#candidate-details-header');
                    if (headerEl) {
                        const firstRemaining = editorElement.querySelector('span[id^="candidate-"]:not(#candidate-details-header)');
                        if (firstRemaining && headerEl.nextSibling === firstRemaining) {
                            const br = document.createElement('br');
                            headerEl.parentNode.insertBefore(br, firstRemaining);
                        }
                    }

                    // If this was the last Key value pair, clean up any orphan <br/> spacers left
                    // between the Hello paragraph and the rest of the content.
                    const remainingSpans = editorElement.querySelectorAll('span[id^="candidate-"]');
                    if (remainingSpans.length === 0) {
                        const allParas = editorElement.querySelectorAll('p');
                        let helloPara: Node | null = null;
                        allParas.forEach(p => {
                            if (p.textContent && p.textContent.trim().toLowerCase() === 'hello,') {
                                helloPara = p;
                            }
                        });
                        if (helloPara) {
                            let sib = (helloPara as Node).nextSibling;
                            while (sib && sib.nodeType === 1 && (sib as HTMLElement).tagName === 'BR') {
                                const nextSib = sib.nextSibling;
                                if (sib.parentNode) { sib.parentNode.removeChild(sib); }
                                sib = nextSib;
                            }
                        }
                    }
                    
                    // Restore cursor position
                    selection.removeAllRanges();
                    selection.addRange(range);
                    this.saveSelection();
                }
            });
        } else {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = this.htmlContent;
            const spanToRemove = tempDiv.querySelector(`span[id='candidate-${candidate.id}']`);
            if (spanToRemove && spanToRemove.parentNode) {
                let prev = spanToRemove.previousSibling;
                if (prev && prev.nodeType === 1 && (prev as HTMLElement).tagName === 'BR') {
                    prev.parentNode.removeChild(prev);
                }
                let next = spanToRemove.nextSibling;
                if (next && next.nodeType === 1 && (next as HTMLElement).outerHTML === '<span>&nbsp;</span>') {
                    next.parentNode.removeChild(next);
                }
                spanToRemove.parentNode.removeChild(spanToRemove);
                this.htmlContent = tempDiv.innerHTML;
            }
        }
        setTimeout(() => {
            // Read live DOM content and emit to parent WITHOUT updating this.htmlContent.
            // causing writeValue() to re-render and reset the cursor.
            const liveContent = editorElement ? editorElement.innerHTML : this.htmlContent;
            this.htmlContentChange.emit(liveContent);
            this.syncCandidateCheckboxesWithEditor(liveContent);
            this.emitSelectedFields();
        }, 100);
    }

        /**
     * Sync candidate checkboxes with editor content. Uncheck if candidate info span is missing
     * or has been emptied (e.g. user manually deleted the text but the wrapper span remained).
     */
    syncCandidateCheckboxesWithEditor(content: string): void {
        if (!this.candidateInformationList || !Array.isArray(this.candidateInformationList)) return;
        const editorElement = document.querySelector('app-ngx-editor [contenteditable="true"]') as HTMLElement;
        this.candidateInformationList.forEach(candidate => {
            let exists: boolean;
            if (editorElement) {
                // Query the live DOM so we can also check whether the span still has content.
                const span = editorElement.querySelector(`span[id='candidate-${candidate.id}']`);
                exists = !!span && (span.textContent || '').trim() !== '';
            } else {
                exists = content.includes(`id="candidate-${candidate.id}"`) || content.includes(`id='candidate-${candidate.id}'`);
            }
            candidate.selected = !!exists;
        });
        // Also update selectAllCandidates checkbox
        this.selectAllCandidates = this.candidateInformationList.every(c => c.selected);
        // Replace the array reference so Angular's child component (include-details-checkboxes)
        this.candidateInformationList = [...this.candidateInformationList];
        // Notify external (e.g. AngularJS) parents about the full selection state so they
        this.candidateSelectionSync.emit(this.candidateInformationList);
        this.cdr.detectChanges();
    }

    // Text Area change handler to keep htmlContent in sync when using textarea for notes

    onNoteChange(event: any) {
        const textarea = event.target;
        const currentViewText = textarea.value;

        this.displayedNote = currentViewText;

        // Sync checkboxes
        if (this.candidateInformationList) {
            this.candidateInformationList.forEach(candidate => {
                const candidateText = `${candidate.name}: ${candidate.value || 'Not Specified'}`;

                // If the specific text block from the checkbox is gone, uncheck it.
                if (candidate.selectedForNote && !currentViewText.includes(candidateText)) {
                    candidate.selectedForNote = false;
                    const key = this.getCandidateKey(candidate);
                    delete this.addedCandidateKeys[key];
                }
            });
            this.selectAllCandidatesNC = this.candidateInformationList.every(c => c.selectedForNote);
        }

        this.htmlContentChange.emit(this.displayedNote);
    }

    onSelectAllCandidatesNC(): void {
        if (this.candidateInformationList && this.candidateInformationList.length > 0) {

            this.candidateInformationList.forEach(candidate => {
                candidate.selectedForNote = this.selectAllCandidatesNC;
            });

            if (this.selectAllCandidatesNC) {
                this.candidateInformationList.forEach(candidate => {
                    this.addCandidateInfoToNote(candidate);
                });
            } else {
                // Remove all candidates
                this.candidateInformationList.forEach(candidate => {
                    this.removeCandidateInfoFromNote(candidate);
                });
            }
        }

        this.htmlContentChange.emit(this.displayedNote);
    }

    onCandidateCheckboxChangeNC(candidate: any): void {
        if (candidate.selectedForNote) {
            // Add candidate info to note
            this.addCandidateInfoToNote(candidate);
        } else {
            // Remove candidate info from note
            this.removeCandidateInfoFromNote(candidate);
        }
        this.htmlContentChange.emit(this.displayedNote);
    }

    addCandidateInfoToNote(candidate: any): void {
        const candidateTextToInsert = `${candidate.name}: ${candidate.value || 'Not Specified'}`;

        this.displayedNote = this.displayedNote || '';

        // If it's already there, don't re-add
        if (this.displayedNote.includes(candidateTextToInsert)) {
            return;
        }

        // Add text logically to the end
        if (this.displayedNote.trim()) {
            this.displayedNote = this.displayedNote.trim() + ', ' + candidateTextToInsert;
        } else {
            this.displayedNote = candidateTextToInsert;
        }

        const key = this.getCandidateKey(candidate);
        this.addedCandidateKeys[key] = true;
    }

    removeCandidateInfoFromNote(candidate: any): void {
        const candidateTextToRemove = `${candidate.name}: ${candidate.value || 'Not Specified'}`;

        if (!this.displayedNote) return;

        let updatedNote = this.displayedNote;

        // Case 1: The user hasn't edited the inserted text, we can remove it exactly.
        if (updatedNote.includes(candidateTextToRemove)) {
            // Try to remove with preceding comma
            const withPreComa = new RegExp(`,?\\s*${this.escapeRegExp(candidateTextToRemove)}`, 'g');
            updatedNote = updatedNote.replace(withPreComa, '');

            // Try to remove with trailing comma if still there
            const withPostComa = new RegExp(`${this.escapeRegExp(candidateTextToRemove)},?\\s*`, 'g');
            updatedNote = updatedNote.replace(withPostComa, '');
        }

        // Cleanup extra commas/spaces
        updatedNote = updatedNote.replace(/,\s*,/g, ',');
        updatedNote = updatedNote.replace(/^,\s*/, '');
        updatedNote = updatedNote.replace(/,\s*$/, '');

        this.displayedNote = updatedNote.trim();
        const key = this.getCandidateKey(candidate);
        delete this.addedCandidateKeys[key];
        this.htmlContentChange.emit(this.displayedNote);
    }

    escapeRegExp(str: string): string {
        return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    getCandidateKey(candidate: any): string {
        // Use candidate name as unique key, sanitized for use in markers
        return candidate.name ? candidate.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '') : 'unknown';
    }

    cleanupCandidateMarkers(note: string): string {
        if (!note) return note;
        let cleanedNote = note;

        // Clean up multiple commas and spaces
        cleanedNote = cleanedNote.replace(/,\s*,/g, ',');
        cleanedNote = cleanedNote.replace(/\s*,\s*/g, ', ');
        cleanedNote = cleanedNote.replace(/\s+/g, ' ');

        // Remove leading/trailing spaces and commas
        cleanedNote = cleanedNote.trim();
        cleanedNote = cleanedNote.replace(/^,\s*/, '').replace(/\s*,$/, '');

        return cleanedNote;
    }

    _syncCandidateDetailsHeader(): void {
        const anySelected = this.candidateInformationList && this.candidateInformationList.some(c => c.selected);
        if (anySelected) {
            this._addCandidateDetailsHeader();
        } else {
            this._removeCandidateDetailsHeader();
        }
    }

    _addCandidateDetailsHeader(): void {
        const editorElement = document.querySelector('app-ngx-editor [contenteditable="true"]') as HTMLElement;
        if (!editorElement) return;
        if (editorElement.querySelector('#candidate-details-header')) return;

        // Use an inline span so the heading lands on the same line as the cursor
        const header = document.createElement('span');
        header.id = 'candidate-details-header';
        header.innerHTML = '<b>Candidate Details:-</b>';

        const firstCandidateSpan = editorElement.querySelector('[id^="candidate-"]');
        const signature = editorElement.querySelector('#signature');
        const insertBefore: Element | null = firstCandidateSpan || signature;

        if (insertBefore && insertBefore.parentNode) {
            // If there is a <br/> immediately before the first candidate span, place the
            // heading before that <br/> so the heading stays on the cursor's line and
            // the <br/> still separates it from the first key-value pair.
            const prevSib = insertBefore.previousSibling;
            if (prevSib && prevSib.nodeType === 1 && (prevSib as HTMLElement).tagName === 'BR') {
                insertBefore.parentNode.insertBefore(header, prevSib);
            } else {
                insertBefore.parentNode.insertBefore(header, insertBefore);
            }
        } else {
            editorElement.appendChild(header);
        }
    }

    _removeCandidateDetailsHeader(): void {
        const editorElement = document.querySelector('app-ngx-editor [contenteditable="true"]') as HTMLElement;
        if (!editorElement) return;
        const header = editorElement.querySelector('#candidate-details-header');
        if (header && header.parentNode) {
            header.parentNode.removeChild(header);
        }
    }

    _updateDocumentsSection(html: string): void {
        const editorElement = document.querySelector('app-ngx-editor [contenteditable="true"]') as HTMLElement;
        if (!editorElement) return;

        const existing = editorElement.querySelector('#email-documents-section') as HTMLElement;
        if (existing && existing.parentNode) {
            existing.parentNode.removeChild(existing);
        }

        if (!html) {
            this.htmlContentChange.emit(editorElement.innerHTML);
            return;
        }

        // If the user placed the cursor in the editor, insert at that position.
        if (this.savedSelection) {
            // Use the Range directly — avoids the Selection API which fails silently
            const range = this.savedSelection.cloneRange();
            range.collapse(false);
            // Strip trailing empty paragraphs so cursor does not land on a blank line.
            const cleanHtml = html.replace(/(\s*<p[^>]*>\s*(<br\s*\/?>)?\s*<\/p>)+\s*$/gi, '');
            // Use a block-level div wrapper — the html contains <p> block elements and putting
            const blockWrapper = document.createElement('div');
            blockWrapper.id = 'email-documents-section';
            blockWrapper.innerHTML = cleanHtml;
            range.insertNode(blockWrapper);
            // Place cursor at the end of the last content node inside the wrapper,
            const lastContent = blockWrapper.lastChild || blockWrapper;
            const newRange = document.createRange();
            newRange.setStartAfter(lastContent);
            newRange.setEndAfter(lastContent);
            this.savedSelection = newRange;
            editorElement.focus();
            const sel = window.getSelection();
            if (sel) {
                sel.removeAllRanges();
                sel.addRange(newRange);
            }
            this.htmlContentChange.emit(editorElement.innerHTML);
            return;
        }

        const cleanFallbackHtml = html.replace(/(\s*<p[^>]*>\s*(<br\s*\/?>)?\s*<\/p>)+\s*$/gi, '');
        const wrapper = document.createElement('div');
        wrapper.id = 'email-documents-section';
        wrapper.innerHTML = cleanFallbackHtml;

        const signature = editorElement.querySelector('#signature') as HTMLElement;
        if (signature && signature.parentNode) {
            signature.parentNode.insertBefore(wrapper, signature);
        } else {
            editorElement.appendChild(wrapper);
        }

        this.htmlContentChange.emit(editorElement.innerHTML);
    }
}
